import React, { useCallback } from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SellIcon from '@mui/icons-material/Sell';
import TimelineIcon from '@mui/icons-material/Timeline';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ChartIcon from '@mui/icons-material/ShowChart';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import PushPinIcon from '@mui/icons-material/PushPin';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import useTouchInteraction from '../../../hooks/useTouchInteraction';
import { shouldEnableOptionChain } from '../../../services/utilities';
import { setPinnedInstrumentsAction } from '../../../store/actions/watchlist';
import { openPriceAlertModal } from '../../../store/actions/alerts';
import { openGTTModal, openBasketModal } from '../../../store/actions/orders';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';

// Helper function to check if instrument is an index
const isIndex = (instrument) => {
  return instrument?.index  === true;
};

const ActionItem = React.memo(({ icon, label, onClick, color, disabled }) => {
  const theme = useTheme();
  const { handlers, touchStates } = useTouchInteraction(onClick, true);

  return (
    <ListItem
      {...handlers}
      disabled={disabled}
      sx={{
        opacity: disabled ? 0.5 : 1,
        backgroundColor: touchStates.isActive && !touchStates.isTouchMoved ? 'action.hover' : 'transparent',
        transition: 'background-color 0.15s ease',
        WebkitTapHighlightColor: 'transparent',
        '&:active': {
          backgroundColor: !touchStates.isTouchMoved ? 'action.hover' : 'transparent'
        },
        py: 1.5
      }}
    >
      <ListItemIcon sx={{ 
        color: color || theme.palette.text.secondary,
        minWidth: 40
      }}>
        {icon}
      </ListItemIcon>
      <ListItemText 
        primary={label}
        primaryTypographyProps={{
          sx: {
            color: color || theme.palette.text.primary,
            fontWeight: 500
          }
        }}
      />
    </ListItem>
  );
});

const MobileActions = React.memo(({
  instrument,
  open,
  onClose,
  onBuy,
  onSell,
  onMarketDepth,
  onDelete,
  userSettings,
  isDefaultWatchlist
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const themeMode = useSelector(state => state.theme.mode);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const pinnedInstruments = useSelector((state) => state.marketwatch.pins) || [];

  const handleCreateAlertClick = useCallback(() => {
    onClose?.();
    dispatch(openPriceAlertModal({
      instrument: {
        token: instrument.token,
        exchange: instrument.exchange,
        trading_symbol: instrument.trading_symbol,
        display_name: instrument.display_name || instrument.trading_symbol,
        price_precision: instrument.price_precision,
        ltp: instrument.ltp,
      },
    }));
  }, [dispatch, instrument, onClose]);

  const handleCreateGTTClick = useCallback(() => {
    onClose?.();
    dispatch(openGTTModal({
      type: 'BUY',
      token: instrument.token,
      exchange: instrument.exchange,
      trading_symbol: instrument.trading_symbol,
      display_name: instrument.display_name || instrument.trading_symbol,
      ltp: instrument.ltp,
    }));
  }, [dispatch, instrument, onClose]);

  const handleAddToBasketClick = useCallback(() => {
    onClose?.();
    dispatch(openBasketModal({
      token: instrument.token,
      exchange: instrument.exchange,
      trading_symbol: instrument.trading_symbol,
      display_name: instrument.display_name || instrument.trading_symbol,
      price_precision: instrument.price_precision,
      ltp: instrument.ltp,
      is_tradable: instrument?.is_tradable ?? instrument?.tradable ?? true,
      lotsize: instrument.lotsize,
      tick_size: instrument.tick_size
    }));
  }, [dispatch, instrument, onClose]);

  const pinIndex = pinnedInstruments.findIndex(
    (pin) =>
      pin &&
      String(pin.token) === String(instrument.token) &&
      String(pin.exchange).toUpperCase().trim() === String(instrument.exchange).toUpperCase().trim()
  );
  const isPinned = pinIndex !== -1;
  const isLocked = pinIndex === 0 || pinIndex === 1;

  const handlePinToSlot = useCallback((targetIndex, e) => {
    e.stopPropagation();

    const filteredPins = pinnedInstruments.filter(
      (pin) =>
        !(pin &&
          String(pin.token) === String(instrument.token) &&
          String(pin.exchange).toUpperCase().trim() === String(instrument.exchange).toUpperCase().trim())
    );

    const newPinObj = {
      display_name: instrument.display_name || instrument.trading_symbol || "",
      exchange: instrument.exchange || "",
      is_index: instrument.is_index ?? false,
      is_tradable: instrument.is_tradable ?? true,
      token: instrument.token,
      trading_symbol: instrument.trading_symbol || "",
    };

    filteredPins[targetIndex] = newPinObj;

    dispatch(setPinnedInstrumentsAction(filteredPins))
      .then(() => {
        enqueueSnackbar(
          `Pinned ${instrument.display_name || instrument.trading_symbol} to Slot ${targetIndex + 1}`,
          { variant: "success" }
        );
      })
      .catch((err) => {
        enqueueSnackbar(err.message || "Failed to pin instrument", { variant: "error" });
      });

    onClose();
  }, [pinnedInstruments, instrument, dispatch, enqueueSnackbar, onClose]);

  const handleUnpin = useCallback((pinIdx, e) => {
    e.stopPropagation();
    if (pinIdx === 0 || pinIdx === 1) {
      enqueueSnackbar("The first two pinned instruments cannot be unpinned.", { variant: "warning" });
      onClose();
      return;
    }

    const newPins = pinnedInstruments.filter((_, idx) => idx !== pinIdx);
    dispatch(setPinnedInstrumentsAction(newPins))
      .then(() => {
        enqueueSnackbar(
          `Unpinned ${instrument.display_name || instrument.trading_symbol}`,
          { variant: "success" }
        );
      })
      .catch((err) => {
        enqueueSnackbar(err.message || "Failed to unpin instrument", { variant: "error" });
      });

    onClose();
  }, [pinnedInstruments, instrument, dispatch, enqueueSnackbar, onClose]);

  const { handlers: closeHandlers, touchStates: closeTouchStates } = useTouchInteraction(onClose, true);

  const handleOptionChainClick = useCallback((e) => {
    e.stopPropagation();
    if (instrument?.exchange && instrument?.token) {
      const pricePrecision = (instrument?.price_precision !== undefined && instrument?.price_precision !== null && instrument?.price_precision !== '')
        ? instrument.price_precision
        : 2;
      navigate(`/overview/tdv/${instrument.exchange}/${instrument.token}/${pricePrecision}`, {
        state: { defaultTab: 'chart' }
      });
      onClose();
    }
  }, [instrument?.exchange, instrument?.token, instrument?.price_precision, navigate, onClose]);

  const handleBuyClick = useCallback((e) => {
    e.stopPropagation();
    onBuy(instrument, e, userSettings);
    onClose();
  }, [instrument, onBuy, onClose]);

  const handleSellClick = useCallback((e) => {
    e.stopPropagation();
    onSell(instrument, e, userSettings);
    onClose();
  }, [instrument, onSell, onClose]);

  const handleMarketDepthClick = useCallback((e) => {
    e.stopPropagation();
    onMarketDepth(e);
    onClose();
  }, [onMarketDepth, onClose]);

  const handleDeleteClick = useCallback((e) => {
    e.stopPropagation();
    onDelete(e);
    onClose();
  }, [onDelete, onClose]);

  const isInstrumentIndex = isIndex(instrument);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: '75vh',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: themeMode === 'dark' 
            ? theme.palette.background.paper 
            : theme.palette.background.default,
          color: theme.palette.text.primary,
          overflow: 'hidden'
        }
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)'
        },
        onClick: onClose,
        transitionDuration: 0
      }}
      transitionDuration={150}
      disableScrollLock
      slotProps={{
        backdrop: {
          invisible: false,
          sx: {
            WebkitTapHighlightColor: 'transparent'
          }
        }
      }}
    >
      <Box sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: 'inherit',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        boxShadow: theme.shadows[2],
        pb: 0.5
      }}>
        <Box
          {...closeHandlers}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor: closeTouchStates.isActive && !closeTouchStates.isTouchMoved ? 'action.hover' : 'transparent',
            transition: 'background-color 0.15s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:active': {
              backgroundColor: !closeTouchStates.isTouchMoved ? 'action.hover' : 'transparent'
            }
          }}
        >
          <CloseIcon sx={{ 
            color: theme.palette.text.secondary,
            pointerEvents: 'none'
          }} />
        </Box>

        <Typography 
          variant="subtitle1" 
          sx={{ 
            px: 2, 
            py: 1.5, 
            fontWeight: 600,
            color: theme.palette.text.primary
          }}
        >
          {instrument?.display_name}
        </Typography>
      </Box>

      <List sx={{
        pt: 0,
        pb: 2,
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        backgroundColor: 'inherit',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
        <ActionItem
          icon={<ExpandMoreIcon />}
          label="Market Depth"
          onClick={handleMarketDepthClick}
        />

        {!isInstrumentIndex && (
          <ActionItem
            icon={<ShoppingCartIcon />}
            label="Buy"
            onClick={handleBuyClick}
            color={theme.palette.success.main}
            disabled={!instrument?.tradable}
          />
        )}

        {!isInstrumentIndex && (
          <ActionItem
            icon={<SellIcon />}
            label="Sell"
            onClick={handleSellClick}
            color={theme.palette.error.main}
            disabled={!instrument?.tradable}
          />
        )}

        {/* Hide chart option for CDS exchange */}
          <ActionItem
            icon={<TimelineIcon />}
            color={theme.palette.primary.main}
            label={shouldEnableOptionChain(instrument) ? "Option Chain + Charts" : "Charts"}
            onClick={handleOptionChainClick}
          />
        

        <ActionItem
          icon={<PushPinIcon style={{ transform: 'rotate(45deg)' }} />}
          label={`Pin to Slot 1 ${pinnedInstruments[0] ? `(${pinnedInstruments[0].display_name})` : ''}`}
          onClick={(e) => handlePinToSlot(0, e)}
          color={theme.palette.primary.main}
          disabled={pinIndex === 0}
        />

        <ActionItem
          icon={<PushPinIcon style={{ transform: 'rotate(45deg)' }} />}
          label={`Pin to Slot 2 ${pinnedInstruments[1] ? `(${pinnedInstruments[1].display_name})` : ''}`}
          onClick={(e) => handlePinToSlot(1, e)}
          color={theme.palette.primary.main}
          disabled={pinIndex === 1}
        />

        {isPinned && !isLocked && (
          <ActionItem
            icon={<PushPinIcon />}
            label="Unpin Instrument"
            onClick={(e) => handleUnpin(pinIndex, e)}
            color={theme.palette.primary.main}
          />
        )}

        <ActionItem
          icon={<NotificationsActiveOutlinedIcon />}
          label="Create Price Alert"
          onClick={handleCreateAlertClick}
          color={theme.palette.primary.main}
        />

        <ActionItem
          icon={<AccessTimeIcon />}
          label="Create GTT"
          onClick={handleCreateGTTClick}
          color={theme.palette.primary.main}
        />

        {!isInstrumentIndex && (
          <ActionItem
            icon={<ShoppingBasketIcon />}
            label="Add to Basket"
            onClick={handleAddToBasketClick}
            color={theme.palette.primary.main}
            disabled={!instrument?.tradable}
          />
        )}

        {!isDefaultWatchlist && (
          <ActionItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick}
            color={theme.palette.error.main}
          />
        )}
      </List>
    </Drawer>
  );
});

export default MobileActions; 